import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { buttonClasses, IconButton } from '@mui/material';
import Tooltip from '@src/components/Tooltip';
import { COLOR } from '@src/styles/color';
import Typography from '@src/new-components/Typography';
import CommonButton from '@src/new-components/Button';
import CommonDialog from '@src/new-components/Dialog/CommonDialog';
import { FEATURE_KEYS } from '@src/configs/featureKeys';
import useFeatureFlags from '@src/hooks/useFeatureFlags';
import useLocation from '@src/hooks/useLocation';
import { CloseRounded as CloseRoundedIcon } from '@mui/icons-material';
import KeyboardArrowRightRoundedIcon from '@mui/icons-material/KeyboardArrowRightRounded';
import sanitizeHtml from 'sanitize-html';
import { MEDIA_FRONTEND_URL } from '@src/configs';
import {
  StyledCreditIntroDialogContent,
  StyledCreditIntroDialogAction,
  StyledCreditIntroImage,
} from './index.style';
import NewCreditIntroContent from './NewCreditIntroContent';

const CreditIntro = ({ isMobile }) => {
  const { t } = useTranslation();
  const { getFeatureValue } = useFeatureFlags();

  const { user } = useSelector((state) => state.auth);
  const { ip } = useLocation();
  const [openCreditIntroDialog, setOpenCreditIntroDialog] = useState(false);

  const creditIntroConfig = getFeatureValue(FEATURE_KEYS.CREDITS_INTRO, {
    userId: user.id,
    email: user.email,
  });

  const creditsUsageGuideEnabled = getFeatureValue(
    FEATURE_KEYS.CREDITS_USAGE_GUIDE,
    {
      userId: user.id,
      email: user.email,
      phoneNumber: user.phoneNumber,
      ip,
    },
  );

  const creditIntroEnabled =
    creditIntroConfig !== undefined &&
    Object.hasOwn(creditIntroConfig, 'releaseDate');

  const handleCloseCreditIntroDialog = () => {
    setOpenCreditIntroDialog(false);
  };
  const handleOpenCreditIntroDialog = () => {
    setOpenCreditIntroDialog(true);
  };

  const renderDialogContent = () => {
    if (creditsUsageGuideEnabled) {
      return <NewCreditIntroContent isMobile={isMobile} />;
    }

    return (
      <>
        <StyledCreditIntroImage isMobile={isMobile}>
          <div className="image-item">
            <img
              src={`${MEDIA_FRONTEND_URL}/images/credits/credits-intro.webp`}
              alt="credit-intro"
            />
          </div>
        </StyledCreditIntroImage>
        <StyledCreditIntroDialogContent isMobile={isMobile}>
          <div className="app-info">
            <div className="section-1">
              <div className="section-1-title">
                <Typography
                  variant={isMobile ? 'body-16-bold' : 'heading-32-bold'}
                  color={COLOR.label2[100]}
                  style={{ textAlign: 'left' }}
                >
                  {t('creditGuideTitle')}
                </Typography>
                <div className="new-item">
                  <Typography
                    variant="body-14-medium"
                    color={COLOR.label2[100]}
                  >
                    <span className="new-text">{t('new')}</span>
                  </Typography>
                </div>
              </div>
              <div className="section-1-item-text">
                <Typography
                  variant={isMobile ? 'body-12-regular' : 'body-14-regular'}
                  color={COLOR.label2[64]}
                  dangerouslySetInnerHTML={{
                    __html: sanitizeHtml(
                      t('creditGuideTitle1', {
                        releaseDate: creditIntroConfig.releaseDate,
                      }),
                      {
                        allowedTags: ['span', 'br'],
                        allowedAttributes: {
                          span: ['style'],
                        },
                      },
                    ),
                  }}
                />
              </div>
            </div>
            <div className="section-2-item-text">
              <img
                src={`${MEDIA_FRONTEND_URL}/images/credits/credits-intro-bell.webp`}
                alt="bell"
                style={{ width: '20px', height: '20px' }}
              />
              <Typography
                variant={isMobile ? 'body-12-regular' : 'body-14-regular'}
                color={COLOR.label2[100]}
                dangerouslySetInnerHTML={{
                  __html: sanitizeHtml(t('creditGuideTitle2'), {
                    allowedTags: ['strong'],
                  }),
                }}
              />
            </div>
          </div>
        </StyledCreditIntroDialogContent>
      </>
    );
  };

  const renderDialogAction = () => {
    if (creditsUsageGuideEnabled) return null;

    return (
      <StyledCreditIntroDialogAction isMobile={isMobile}>
        <div className="button-1">
          <CommonButton
            variant="contained"
            onClick={handleCloseCreditIntroDialog}
            customStyle={{
              height: '40px',
              padding: '8px 24px',
              borderRadius: '8px',
            }}
          >
            <Typography variant="body-14-semibold" color={COLOR.indigo.default}>
              {t('creditGuideButton2')}
            </Typography>
          </CommonButton>
        </div>
      </StyledCreditIntroDialogAction>
    );
  };

  const renderCreditIntroButton = () => (
    <Tooltip arrow title={t('creditIntroHover')} placement="bottom">
      <span style={{ display: 'inline-flex' }}>
        <CommonButton
          variant="outlineSmall"
          sx={{
            height: '40px',
            minWidth: '40px',
            borderRadius: '12px',
            border: '1px solid transparent',
            padding: '0px',
            background: `linear-gradient(white, white), padding-box
                    ${COLOR.rainbow} border-box`,
            backgroundClip: 'padding-box, border-box',
            [`& .${buttonClasses.startIcon}`]: {
              margin: 0,
            },
          }}
          startIcon={
            <img
              src={`${MEDIA_FRONTEND_URL}/images/credits/credits-intro-icon.webp`}
              alt="sparkle"
              style={{
                height: '20px',
                width: '20px',
              }}
            />
          }
          onClick={handleOpenCreditIntroDialog}
        />
      </span>
    </Tooltip>
  );

  const renderCreditIntroButtonMobile = () => (
    <CommonButton
      variant="outlineSmall"
      sx={{
        height: '40px',
        minWidth: '40px',
        borderRadius: '8px',
        border: '1px solid transparent',
        background: `linear-gradient(white, white), padding-box
                    ${COLOR.rainbow} border-box`,
        backgroundClip: 'padding-box, border-box',
        width: '166.5px',
        padding: '8px',
      }}
      startIcon={
        <img
          src={`${MEDIA_FRONTEND_URL}/images/credits/credits-intro-icon.webp`}
          alt="sparkle"
          style={{
            height: '16px',
            width: '16px',
          }}
        />
      }
      onClick={handleOpenCreditIntroDialog}
      endIcon={
        <KeyboardArrowRightRoundedIcon
          sx={{
            fontSize: 24,
            color: COLOR.navyBlue,
            width: '16px',
            height: '16px',
          }}
        />
      }
    >
      <Typography variant="body-14-semibold" noWrap color={COLOR.label2[100]}>
        {t('creditIntroButtonText')}
      </Typography>
    </CommonButton>
  );

  const getDialogWidth = () => {
    if (isMobile) return '343px !important';
    return creditsUsageGuideEnabled ? '860px' : '720px';
  };

  const getDialogSx = () => {
    if (creditsUsageGuideEnabled) {
      if (isMobile) {
        return {
          '& .MuiDialog-paper': {
            minWidth: '343px !important',
            borderRadius: '16px',
          },
          '& .MuiDialogTitle-root': {
            padding: '12px 16px !important',
            height: '56px',
            boxSizing: 'border-box',
            display: 'flex',
            alignItems: 'center',
            borderBottom: `1px solid ${COLOR.label[16]}`,
          },
          '& .MuiDialogContent-root': {
            padding: '12px !important',
          },
        };
      }
      return {
        '& .MuiDialog-paper': {
          width: '860px !important',
          maxWidth: '860px !important',
          height: '720px',
          borderRadius: '16px',
          boxShadow: `0px 4px 16px 0px ${COLOR.black[8]}`,
        },
        '& .MuiDialogTitle-root': {
          padding: '12px 24px !important',
          height: '56px',
          boxSizing: 'border-box',
          display: 'flex',
          alignItems: 'center',
          borderBottom: `1px solid ${COLOR.label[16]}`,
        },
        '& .MuiDialogContent-root': {
          padding: '24px !important',
          overflowY: 'auto',
        },
      };
    }
    if (isMobile) {
      return {
        '& .MuiDialog-paper': {
          minWidth: '343px !important',
        },
        '& .MuiDialogContent-root': {
          padding: '16px !important',
        },
        '& .MuiDialogActions-root': {
          padding: '0px 16px 16px !important',
        },
      };
    }
    return {
      '& .MuiDialogActions-root': {
        padding: '0px 32px 24px !important',
      },
    };
  };

  const renderNewCloseIcon = () => (
    <IconButton
      style={{
        position: 'absolute',
        right: isMobile ? '16px' : '24px',
        top: '16px',
        width: '24px',
        height: '24px',
        padding: 0,
        zIndex: 10,
      }}
      onClick={handleCloseCreditIntroDialog}
      aria-label="close"
    >
      <CloseRoundedIcon style={{ fontSize: '24px' }} />
    </IconButton>
  );

  const renderLegacyCloseIcon = () => (
    <IconButton
      style={{
        color: 'white',
        float: 'right',
        marginLeft: 'auto',
        position: 'absolute',
        right: isMobile ? '12px' : '24px',
        top: isMobile ? '12px' : '24px',
        zIndex: 1000,
        width: isMobile ? '32px' : '40px',
        height: isMobile ? '32px' : '40px',
        border: '0.5px solid #FFFFFF7A',
        backgroundColor: '#FFFFFF0A',
        backdropFilter: 'blur(16px)',
      }}
      onClick={handleCloseCreditIntroDialog}
    >
      <CloseRoundedIcon />
    </IconButton>
  );

  return creditIntroEnabled ? (
    <>
      {isMobile ? renderCreditIntroButtonMobile() : renderCreditIntroButton()}

      <CommonDialog
        width={getDialogWidth()}
        open={openCreditIntroDialog}
        showDivider={!!creditsUsageGuideEnabled}
        hideTitle={!creditsUsageGuideEnabled}
        title={creditsUsageGuideEnabled ? t('creditIntroHover') : undefined}
        onClose={handleCloseCreditIntroDialog}
        contentComponent={renderDialogContent()}
        actionComponent={renderDialogAction()}
        renderCloseIcon={
          creditsUsageGuideEnabled
            ? renderNewCloseIcon()
            : renderLegacyCloseIcon()
        }
        styleContent={{
          padding: isMobile ? '16px' : '24px',
        }}
        sx={getDialogSx()}
      />
    </>
  ) : (
    <></>
  );
};

export default CreditIntro;
